import { bangkokDayKey } from './journeyTime'
import { isMessage, isVisit } from './journeyDays'

const CHECKOUT_FAILED = 'checkout_failed'
const SELECT_PAYMENT_METHOD = 'select_payment_method'
const CART_NAMES = ['add_to_cart', 'update_cart']
const RESULT_LADDER = [
  ['submitted', ['checkout_submitted']],
  ['checkoutFailed', ['checkout_failed']],
  ['beganCheckout', ['begin_checkout']],
  ['cart', ['add_to_cart', 'update_cart', 'remove_from_cart', 'view_cart']],
  ['viewed', ['view_item', 'select_item', 'add_to_wishlist']],
  ['searched', ['search']]
]

const allSteps = (steps) => (steps || []).flatMap((step) => (step.steps?.length ? step.steps : [step]))

const orderItemIds = (lineItems) =>
  new Set((lineItems || []).map((lineItem) => lineItem?.item?.externalId).filter(Boolean).map(String))

const productsOf = (step, items, orderIds) =>
  (step.item_ids || []).map((id) => ({
    id: String(id),
    name: items?.[id]?.name || null,
    inOrder: orderIds.has(String(id))
  }))

const productsNotInOrder = (journey, orderIds) => {
  const seen = new Set()
  ;(journey.steps || []).forEach((step) => (step.item_ids || []).forEach((id) => seen.add(String(id))))
  return [...seen]
    .filter((id) => !orderIds.has(id))
    .map((id) => ({ id, name: journey.items?.[id]?.name || null }))
}

const failedCheckouts = (steps) => {
  const failed = allSteps(steps).filter((step) => step.name === CHECKOUT_FAILED)
  return {
    count: failed.reduce((count, step) => count + (step.count || 1), 0),
    reasons: [...new Set(failed.flatMap((step) => step.details || []))]
  }
}

const activityOf = (steps) => {
  const visits = (steps || []).filter(isVisit)
  const days = [...new Set(visits.map((visit) => bangkokDayKey(visit.first_at)).filter(Boolean))]
  if (!visits.length) return null
  return { visits: visits.length, days: days.length, firstAt: visits[0].first_at }
}

const visitResultOf = (visit) => {
  const names = new Set((visit.steps || []).map((step) => step.name))
  const found = RESULT_LADDER.find(([, stepNames]) => stepNames.some((name) => names.has(name)))
  return found ? found[0] : null
}

const visitSearches = (visit) => [
  ...new Set((visit.steps || []).filter((step) => step.name === 'search').flatMap((step) => step.details || []))
]

const visitTotals = (visit) => {
  const totals = []
  ;(visit.steps || []).forEach((step) => {
    const found = totals.find((total) => total.name === step.name)
    if (found) found.count += step.count || 1
    else totals.push({ name: step.name, count: step.count || 1 })
  })
  return totals
}

const visitProducts = (visit, items, orderIds) => {
  const carted = new Set(
    (visit.steps || []).filter((step) => CART_NAMES.includes(step.name)).flatMap((step) => step.item_ids || []).map(String)
  )
  return productsOf(visit, items, orderIds)
    .map((product) => ({ ...product, carted: carted.has(product.id) }))
    .sort((a, b) => Number(b.carted) - Number(a.carted))
}

const minutesBetween = (firstAt, lastAt) => Math.round((Date.parse(lastAt) - Date.parse(firstAt)) / 60000)

// osh-journey trims every step detail and cuts it to 80 characters (DETAIL_CHARS).
const DETAIL_CHARS = 80
const detailOf = (text) => [...(text || '').trim()].slice(0, DETAIL_CHARS).join('')

const creditedMessage = (steps, attribution, startedAt) => {
  if (!attribution || attribution.holdout || attribution.colleague) return null
  const exposedAt = Date.parse(attribution.exposed_at)
  if (exposedAt > Date.parse(startedAt)) return null
  const flow = detailOf(attribution.flow_name)
  const messages = steps.filter(
    (step) => isMessage(step) && Date.parse(step.last_at) >= exposedAt && (step.details || []).includes(flow)
  )
  return messages.length ? messages[messages.length - 1] : null
}

const quoteOpensOf = (journey) => {
  const views = journey.quote_views
  return views && views.count > 0 ? views : null
}

export {
  CHECKOUT_FAILED,
  SELECT_PAYMENT_METHOD,
  allSteps,
  visitResultOf,
  visitSearches,
  visitTotals,
  visitProducts,
  minutesBetween,
  orderItemIds,
  productsOf,
  productsNotInOrder,
  failedCheckouts,
  activityOf,
  creditedMessage,
  quoteOpensOf
}
